import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * W3C Trace Context. ASP.NET Core parses this header and adopts the trace id as
 * the Activity's, which Serilog then writes to every log line as `@tr`. So the
 * browser chooses the id, and one request's server-side lines group themselves —
 * no bespoke correlation header needed on either side.
 */
export const TRACEPARENT_HEADER = 'traceparent';

/**
 * Ours. A trace covers exactly one HTTP round trip, so it can never connect a
 * product search, a variant fetch and the save that failed. This is the only
 * identifier that spans a visit.
 */
export const SESSION_ID_HEADER = 'X-Session-Id';

const SESSION_STORAGE_KEY = 'sh-session-id';

/** 32 hex characters — the trace-id length, and what the backend's sanitiser accepts. */
function randomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // Older WebViews on POS terminals may not expose crypto. These ids need to be
    // unique, not unguessable, so Math.random is an acceptable fallback.
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Builds a W3C traceparent: `00-<32 hex trace>-<16 hex span>-01`.
 *
 * The format is strict. Wrong version, wrong length, or an all-zero id and .NET
 * silently ignores the header and generates its own — no error, just a mismatch
 * that is confusing to debug later. The trailing `01` marks it sampled.
 */
export function newTraceparent(): string {
  return `00-${randomHex(32)}-${randomHex(16)}-01`;
}

/**
 * Stable for as long as the tab lives.
 *
 * sessionStorage, not localStorage: a session is this tab and this visit.
 * localStorage would persist for days and merge unrelated visits into one
 * "session", which defeats the point of having one.
 */
export function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!id) {
      id = randomHex(32);
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }

    return id;
  } catch {
    // Private browsing, or storage disabled by policy. A per-request id is worse
    // than a stable one but better than failing the request.
    return randomHex(32);
  }
}

/**
 * Stamps every API request so the browser and the server agree on how to identify
 * it, and so a whole visit can be followed across requests.
 *
 * Must be registered FIRST in withInterceptors() so requests the other
 * interceptors modify still carry the headers.
 */
export const traceInterceptor: HttpInterceptorFn = (req, next) => {
  // Keycloak and CDN calls are not ours to annotate, and trace context should not
  // leak to third parties.
  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }

  return next(
    req.clone({
      headers: req.headers
        .set(TRACEPARENT_HEADER, newTraceparent())
        .set(SESSION_ID_HEADER, getSessionId()),
    }),
  );
};
